import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { AdminService } from '../../../../services/admin.service';
import { CommonService } from '../../../../services/common.service';

interface Plant {
  secNo: string;
  section: string;
  plantNo: string;
  plant: string;
  oeriDiv: string;
  oeriSec: string;
  oeriPlant: string;
  oeriArea: string;
  oeriLocPath: string;
  mxArea: string;
  mxAreaName: string;
  mxSec: string | null;
  mxSecName: string | null;
  mxUnit: string;
  mxUnitName: string;
  mxLocPath: string;
}

interface Division {
  id: string;
  name: string;
  expanded: boolean;
  plants?: Plant[];
}

@Component({
    selector: 'app-display-hierarchy',
    imports: [
        CommonModule,
        ButtonModule,
        TableModule,
        TooltipModule
    ],
    templateUrl: './display-hierarchy.component.html',
    styleUrl: './display-hierarchy.component.scss'
})
export class DisplayHierarchyComponent implements OnInit {
  siteId: string = 'DRES';
  divisions: Division[] = [];
  expandedRows: { [s: string]: boolean } = {};
  maximoData: any;
  allPlants: Plant[] = []; // To store all plants for Excel export
  
  constructor(
    private adminService: AdminService,
    private commonService: CommonService
  ) {}
  
  ngOnInit() {
    // Call getTLRMaximoData from AdminService
    this.adminService.getTLRMaximoData().subscribe((response: any) => {
      this.maximoData = response;
      
      // Process the response data
      if (response && response.division && response.details) {
        this.processMaximoData(response.division, response.details);
      }
    });
  }
  
  processMaximoData(divisions: any[], details: any[]) {
    // Create a map of division data for quick lookup
    const divisionMap = new Map<string, any>();
    divisions.forEach(div => {
      divisionMap.set(div.tlrDivisionNo.trim(), div);
    });
    
    // Group details by division number
    const divisionDetailsMap = new Map<string, any[]>();
    details.forEach(item => {
      const divisionNo = item.tlrDivisionNo?.trim();
      if (!divisionNo) return;
      
      if (!divisionDetailsMap.has(divisionNo)) {
        divisionDetailsMap.set(divisionNo, []);
      }
      
      divisionDetailsMap.get(divisionNo)?.push(item);
    });
    
    // Create division objects
    this.divisions = Array.from(divisionMap.entries()).map(([divisionNo, divObj]) => {
      const divDetails = divisionDetailsMap.get(divisionNo) || [];
      const plants = this.mapToPlants(divDetails);
      
      // Add to allPlants for Excel export
      this.allPlants = [...this.allPlants, ...plants];
      
      // Create division
      const division: Division = {
        id: divisionNo,
        name: `${divisionNo} (${divObj.tlrDivision})`,
        expanded: false,
        plants: plants
      };
      
      return division;
    });
  }
  
  mapToPlants(items: any[]): Plant[] {
    // Convert each item directly to a Plant object
    return items.map(item => ({
      secNo: item.tlrSectionNo || '',
      section: (item.tlrSection || '').trim(),
      plantNo: item.tlrPlantNo || '',
      plant: item.tlrPlant || '',
      oeriDiv: item.oeriDivision || '',
      oeriSec: item.oeriSection || '',
      oeriPlant: item.oeriPlant || '',
      oeriArea: item.oeriArea || '',
      oeriLocPath: item.oeriLocPath || '',
      mxArea: item.mxArea || '',
      mxAreaName: item.mxAreaName || '',
      mxSec: item.mxSection,
      mxSecName: item.mxSectionName,
      mxUnit: item.mxUnit || '',
      mxUnitName: item.mxUnitName || '',
      mxLocPath: item.mxLocPath || ''
    }));
  }
  
  toggleRow(division: Division) {
    division.expanded = !division.expanded;
  }
  
  exportToExcel() {
    if (this.allPlants.length > 0) {
      this.commonService.exportToExcel(this.allPlants, 'TLR_Maximo_OERI_Hierarchy.xlsx');
    } else {
      console.error('No data available to export');
    }
  }
}
